var _excluded = ["dropzoneText", "dropzoneProps", "onDelete", "onUpload", "onSortComplete"];
var _templateObject, _templateObject2;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function _taggedTemplateLiteralLoose(strings, raw) { if (!raw) { raw = strings.slice(0); } strings.raw = raw; return strings; }
import React, { useCallback, useState } from 'react';
import styled, { css } from 'styled-components';
import { uid } from 'react-uid';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import { closestCenter, DragOverlay, DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import PhotoLibraryIcon from '@material-ui/icons/PhotoLibrary';
import Box from './Box';
import Confirmation from './Confirmation';
import Text from './Text';
import GalleryDragOverlay from './GalleryDragOverlay';
import SortableGalleryImage from './SortableGalleryImage';
import Gallery from './Gallery';
var StyledDropzone = styled.div(_templateObject || (_templateObject = _taggedTemplateLiteralLoose(["\n  ", "\n"])), function (_ref) {
  var theme = _ref.theme;
  return css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteralLoose(["\n    border: 1px dashed #627392;\n    background: #f8f8f8;\n    margin: 10px 0 0px 0;\n    border-radius: ", "px;\n    color: #394869;\n  "])), theme.shape.borderRadius);
});
function SortableGallery(_ref2) {
  var dropzoneText = _ref2.dropzoneText,
    dropzoneProps = _ref2.dropzoneProps,
    onDelete = _ref2.onDelete,
    onUpload = _ref2.onUpload,
    onSortComplete = _ref2.onSortComplete,
    listProps = _objectWithoutPropertiesLoose(_ref2, _excluded);
  var images = listProps.images,
    loading = listProps.loading,
    infoText = listProps.infoText;
  var _useState = useState(null),
    activeId = _useState[0],
    setActiveId = _useState[1];
  var _useState2 = useState(null),
    confirmDelete = _useState2[0],
    setConfirmDelete = _useState2[1];
  var sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
  var handleUpload = useCallback(function (acceptedFiles) {
    var uploadedFiles = [];
    var promises = [];
    acceptedFiles.forEach(function (file) {
      return promises.push(new Promise(function (resolve) {
        var reader = new FileReader();
        reader.onload = function () {
          uploadedFiles.push({
            path: reader.result,
            thumb: reader.result,
            name: file.path,
            type: file.type
          });
          resolve();
        };
        reader.readAsDataURL(file);
      }));
    });
    Promise.all(promises).then(function () {
      onUpload(uploadedFiles);
    });
  }, [onUpload]);
  var handleDragStart = function handleDragStart(event) {
    setActiveId(event.active.id);
  };
  var handleDragCancel = function handleDragCancel() {
    setActiveId(null);
  };
  var handleDragEnd = function handleDragEnd(event) {
    var active = event.active,
      over = event.over;
    if (active.id !== over.id) {
      var oldIndex = images.findIndex(function (img) {
        return img.thumb === active.id;
      });
      var newIndex = images.findIndex(function (img) {
        return img.thumb === over.id;
      });
      var sortedImages = arrayMove(images, oldIndex, newIndex);
      onSortComplete(sortedImages, newIndex, oldIndex);
      setActiveId(null);
    }
  };
  var handleDelete = function handleDelete(e, index) {
    e.stopPropagation();
    e.preventDefault();
    setConfirmDelete(index);
    return false;
  };
  var handleDeleteConfirm = function handleDeleteConfirm() {
    onDelete(confirmDelete);
    setConfirmDelete(null);
  };
  return /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(DndContext, {
    sensors: sensors,
    collisionDetection: closestCenter,
    onDragCancel: handleDragCancel,
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd
  }, /*#__PURE__*/React.createElement(SortableContext, {
    items: images.map(function (image) {
      return image.thumb;
    }),
    strategy: rectSortingStrategy
  }, /*#__PURE__*/React.createElement(Gallery, {
    loading: loading,
    images: images,
    infoText: infoText,
    renderItem: function renderItem(image, i) {
      return /*#__PURE__*/React.createElement(SortableGalleryImage, {
        key: uid(image, i),
        src: image.thumb,
        caption: i === 0 ? 'Main Photo' : i + 1,
        onDelete: function onDelete(e) {
          return handleDelete(e, i);
        },
        uploading: !!image.uploading,
        onClick: image.onClick
      });
    }
  })), /*#__PURE__*/React.createElement(DragOverlay, {
    adjustScale: false
  }, activeId ? /*#__PURE__*/React.createElement(GalleryDragOverlay, {
    src: activeId
  }) : null)), /*#__PURE__*/React.createElement(Dropzone, _extends({}, _extends({
    disabled: loading
  }, dropzoneProps), {
    accept: "image/*",
    onDrop: handleUpload
  }), function (_ref3) {
    var getRootProps = _ref3.getRootProps,
      getInputProps = _ref3.getInputProps;
    return /*#__PURE__*/React.createElement(StyledDropzone, getRootProps(), /*#__PURE__*/React.createElement("input", getInputProps()), /*#__PURE__*/React.createElement(Box, {
      display: "flex",
      align: "center",
      justify: "center",
      height: 65
    }, /*#__PURE__*/React.createElement(PhotoLibraryIcon, {
      fontSize: "small"
    }), " \xA0", /*#__PURE__*/React.createElement(Text, {
      variant: "body2"
    }, dropzoneText)));
  }), /*#__PURE__*/React.createElement(Confirmation, {
    open: confirmDelete !== null,
    title: "Please Confirm",
    message: "Are you sure you want to remove this photo?",
    onCancel: function onCancel() {
      return setConfirmDelete(null);
    },
    onConfirm: handleDeleteConfirm
  }));
}
SortableGallery.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Default text dispalyed on dropzone area
   */
  dropzoneText: PropTypes.string,
  /**
   * Props for `Dropzone`
   * @see https://react-dropzone.js.org/#src
   */
  dropzoneProps: PropTypes.object,
  /**
   * List of images
   */
  images: PropTypes.arrayOf(PropTypes.shape({
    path: PropTypes.string,
    thumb: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string
  })),
  /**
   *
   */
  infoText: PropTypes.node,
  /**
   *
   */
  loading: PropTypes.bool,
  /**
   * Callback that is invoked when `delete` action is requested ends.
   */
  onDelete: PropTypes.func,
  /**
   * Callback that is invoked when sorting ends.
   */
  onSortComplete: PropTypes.func,
  /**
   * Callback that is invoked when `save` action is requested ends.
   */
  onUpload: PropTypes.func
} : {};
SortableGallery.defaultProps = {
  dropzoneText: 'Drag and drop photos',
  dropzoneProps: {},
  images: [],
  infoText: undefined,
  loading: false,
  onDelete: undefined,
  onSortComplete: undefined,
  onUpload: undefined
};
export default SortableGallery;